import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Trash2, Eye, EyeOff, Save, Plus, Edit3, AlertTriangle, Copy, Sparkles, Loader2, X, CheckCircle2, XCircle } from "lucide-react";
import { CATEGORY_LABEL_KEYS } from "@/data/journalArticles";
import { TAG_GROUPS } from "@/data/journalTags";
import AdminShell from "@/components/admin/AdminShell";
import TranslationBadge from "@/components/admin/TranslationBadge";
import CleanupReportPanel from "@/components/admin/CleanupReportPanel";
import { ADMIN_TOKEN_KEY, readAdminToken, clearAdminToken } from "@/components/admin/AdminGuard";
import { translationStatus } from "@/lib/localize";
import { useT } from "@/i18n/LanguageContext";
import AssetPicker from "@/components/vault/AssetPicker";
import { adminApi } from "@/lib/adminApi";
import VaultImage from "@/components/vault/VaultImage";

/**
 * /admin/journal — Founder-only editor.
 *
 * Paste text from AutoSEO (title, cover image URL, Markdown body), pick a
 * category + tags, then save as draft or publish. Drafts are hidden from the
 * public journal until explicitly published.
 *
 * Auth is a bearer token entered on the panel and stored in localStorage.
 * All admin endpoints validate against ADMIN_TOKEN server-side.
 */

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const TOKEN_KEY = ADMIN_TOKEN_KEY;

const CATEGORIES = [
  "marcus-aurelius",
  "epictetus",
  "seneca",
  "stoic-practice",
  "virtues",
  "modern-life",
  "virtue-guide",
];

const EMPTY_FORM = {
  id: null,
  slug: "",
  category: "stoic-practice",
  tags: "",
  cover: "",
  cover_content_id: "",
  author_name: "Ed Korporaal",
  author_role: "Founder, StoicOs.ai",
  reading_time_min: 5,
  title_en: "",
  title_nl: "",
  excerpt_en: "",
  excerpt_nl: "",
  body_en: "",
  body_nl: "",
  meta_en: "",
  meta_nl: "",
  featured: false,
  founder_approved_en: false,
  founder_approved_nl: false,
};

async function api(method, path, token, body) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // Try to pull `detail` out of a FastAPI JSON error body so the caller
    // sees the human-readable reason (e.g. "LLM call failed: rate limit …")
    // instead of a raw JSON blob. Raw HTML error pages (Cloudflare 504) are
    // NEVER surfaced — they collapse to a short readable message.
    const looksHtml = /^\s*</.test(text) || /<html|<!doctype/i.test(text);
    let detail = looksHtml ? "" : text;
    if (!looksHtml) {
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === "object" && parsed.detail) {
          if (typeof parsed.detail === "string") detail = parsed.detail;
          else detail = parsed.detail.message || JSON.stringify(parsed.detail);
        }
      } catch {
        /* text was not JSON — leave as-is */
      }
    }
    const err = new Error(detail || `De server gaf een foutmelding (HTTP ${res.status}). Probeer het opnieuw.`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  return res.json();
}

function toPayload(f) {
  return {
    slug: f.slug || null,
    category: f.category,
    tags: (f.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
    cover: f.cover,
    cover_content_id: f.cover_content_id || "",
    author_name: f.author_name,
    author_role: f.author_role,
    reading_time_min: Number(f.reading_time_min) || 5,
    title: { en: f.title_en, nl: f.title_nl },
    excerpt: { en: f.excerpt_en, nl: f.excerpt_nl },
    body: { en: f.body_en, nl: f.body_nl },
    meta_description: { en: f.meta_en, nl: f.meta_nl },
    featured: !!f.featured,
  };
}

function fromDoc(doc) {
  return {
    id: doc.id,
    slug: doc.slug,
    category: doc.category,
    tags: (doc.tags || []).join(", "),
    cover: doc.cover || "",
    cover_content_id: doc.cover_content_id || "",
    author_name: (doc.author && doc.author.name) || "Ed Korporaal",
    author_role: (doc.author && doc.author.role) || "Founder, StoicOs.ai",
    reading_time_min: doc.reading_time_min || 5,
    title_en: (doc.title && doc.title.en) || "",
    title_nl: (doc.title && doc.title.nl) || "",
    excerpt_en: (doc.excerpt && doc.excerpt.en) || "",
    excerpt_nl: (doc.excerpt && doc.excerpt.nl) || "",
    body_en: (doc.body && doc.body.en) || "",
    body_nl: (doc.body && doc.body.nl) || "",
    meta_en: (doc.meta_description && doc.meta_description.en) || "",
    meta_nl: (doc.meta_description && doc.meta_description.nl) || "",
    featured: !!doc.featured,
    source: doc.source || "manual",
    cleanup_report: doc.cleanup_report || null,
    cleanup_passed: !!doc.cleanup_passed,
    content_flags: doc.content_flags || null,
    content_flags_acknowledged: !!doc.content_flags_acknowledged,
    founder_approved_en: !!doc.founder_approved_en,
    founder_approved_nl: !!doc.founder_approved_nl,
  };
}

const WARNING_TYPE_KEYS = {
  promoTerms: "admin.journal.warnings.promo",
  namedEntities: "admin.journal.warnings.named",
  unverified_claim: "admin.journal.warnings.claim",
};

/** Editorial warnings that require explicit founder confirmation before
 * publish: guard flags (minus externalDomains — those are physically removed
 * by the cleanup protocol) + unverified topical claims from the report. */
function collectEditorialWarnings(form) {
  if (form.source !== "autoseo") return [];
  const out = [];
  const flags = { ...(form.content_flags || {}) };
  delete flags.externalDomains;
  for (const [type, items] of Object.entries(flags)) {
    (items || []).forEach((v) => out.push({ type, passage: String(v) }));
  }
  for (const f of (form.cleanup_report && form.cleanup_report.review_flags) || []) {
    out.push({ type: f.type || "unverified_claim", passage: f.passage });
  }
  return out;
}


/**
 * LocalizationHealth panel.
 *
 * Root-cause diagnostic for the Journal-localization P0 ("NL switch
 * shows English content on the public site"). Consumes
 * `GET /api/admin/journal/localization-audit` and renders:
 *
 *  – a summary bar with counts of fully bilingual vs missing-NL articles
 *  – a per-article table showing WHY the article can't render as NL
 *    (empty title.nl, empty body.nl, auto_translate_failed, …) plus a
 *    "Fix" button that either jumps to the article editor or triggers
 *    the bulk-translate flow.
 *
 * Deliberately compact — collapsed by default, expanded when the
 * founder needs to investigate.
 */
function LocalizationHealth({ audit, loading, onRefresh, onOpenBulk, onEditSlug, t }) {
  const [expanded, setExpanded] = useState(false);
  // Per-row retranslate state — { [articleId]: { busy, error } }
  const [retryState, setRetryState] = useState({});

  const handleRetranslate = async (articleId, slug) => {
    setRetryState((s) => ({ ...s, [articleId]: { busy: true, error: null } }));
    try {
      await adminApi.retranslateArticleNL(articleId);
      setRetryState((s) => ({ ...s, [articleId]: { busy: false, error: null } }));
      // Refresh the audit so the row flips from ⚠ to ✓ immediately.
      onRefresh();
    } catch (e) {
      // Backend returns a structured 422 with reason_code + detail
      const d = e?.detail;
      const msg = (d && typeof d === "object")
        ? `${d.reason_code || "error"}: ${d.detail || d.message || "unknown"}`
        : (e?.message || String(e));
      setRetryState((s) => ({ ...s, [articleId]: { busy: false, error: msg } }));
    }
  };

  if (!audit && !loading) return null;

  const missingNlCount = audit?.missing_nl_count || 0;
  const total = audit?.total_published || 0;
  const bilingual = audit?.fully_bilingual || 0;
  const auditFailed = audit?.auto_translate_failed || 0;
  const noAttempt = audit?.en_only_no_attempt || 0;
  const pending = audit?.auto_translate_pending || 0;

  // Overall health color: green if all bilingual, amber if any missing NL.
  const healthy = total > 0 && missingNlCount === 0;
  const bannerCls = healthy
    ? "border-emerald-400/40 bg-emerald-400/5 text-emerald-100"
    : "border-amber-400/50 bg-amber-400/10 text-amber-100";

  // Sort so worst offenders (no NL at all) come first — matches backend.
  const rows = audit?.articles || [];

  return (
    <div
      data-testid="localization-health"
      className={`mb-4 border ${bannerCls} p-3`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase mb-1 opacity-70">
            {t("admin.journal.localization.title") /* "Localization health" */}
          </p>
          <p className="text-[13px]">
            {loading ? (
              t("admin.journal.localization.loading") /* "Analysing…" */
            ) : healthy ? (
              t("admin.journal.localization.healthy", { total }) /* "All {total} published articles are bilingual." */
            ) : (
              t("admin.journal.localization.summary", {
                missing: missingNlCount,
                total,
              }) /* "{missing} of {total} articles miss NL and will fall back to English." */
            )}
          </p>
          {!loading && !healthy && (
            <div className="text-[11px] opacity-80 mt-1 flex flex-wrap gap-x-3 gap-y-0.5" data-testid="localization-health-breakdown">
              {bilingual > 0 && <span data-testid="lh-bilingual">✓ {bilingual} bilingual</span>}
              {noAttempt > 0 && <span data-testid="lh-no-attempt">⚠ {noAttempt} never auto-translated</span>}
              {auditFailed > 0 && <span data-testid="lh-failed">✗ {auditFailed} auto-translate failed</span>}
              {pending > 0 && <span data-testid="lh-pending">… {pending} pending</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {!healthy && (
            <button
              type="button"
              onClick={onOpenBulk}
              data-testid="localization-health-bulk-translate"
              className="text-[10px] tracking-[0.24em] uppercase px-2.5 py-1.5 border border-amber-400/60 text-amber-100 hover:bg-amber-400/20"
            >
              {t("admin.journal.localization.fixAll") /* "Translate missing NL" */}
            </button>
          )}
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            data-testid="localization-health-refresh"
            className="text-[10px] tracking-[0.24em] uppercase px-2.5 py-1.5 border border-current opacity-70 hover:opacity-100 disabled:opacity-30"
          >
            {loading ? "…" : t("admin.journal.localization.refresh") /* "Refresh" */}
          </button>
          {rows.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid="localization-health-toggle"
              className="text-[10px] tracking-[0.24em] uppercase px-2.5 py-1.5 border border-current opacity-70 hover:opacity-100"
            >
              {expanded ? t("admin.journal.localization.collapse") /* "Hide" */ : t("admin.journal.localization.expand") /* "Details" */}
            </button>
          )}
        </div>
      </div>

      {expanded && rows.length > 0 && (
        <div className="mt-3 border-t border-current/20 pt-2 max-h-64 overflow-y-auto text-[11px]" data-testid="localization-health-table">
          <table className="w-full">
            <thead className="text-[9px] tracking-[0.22em] uppercase opacity-60">
              <tr>
                <th className="text-left py-1">{t("admin.journal.localization.col.article") /* "Article" */}</th>
                <th className="text-center py-1 w-8">Title</th>
                <th className="text-center py-1 w-8">Body</th>
                <th className="text-center py-1 w-8">Excerpt</th>
                <th className="text-left py-1">{t("admin.journal.localization.col.reason") /* "Reason" */}</th>
                <th className="py-1 w-14"></th>
              </tr>
            </thead>
            <tbody>
              {rows.filter((r) => !r.renders_all_nl).slice(0, 60).map((r) => {
                const rs = retryState[r.id] || {};
                return (
                <tr key={r.id} data-testid={`lh-row-${r.slug}`} className="border-t border-current/10">
                  <td className="py-1 pr-2">
                    <div className="truncate max-w-[260px]" title={r.title_en_preview}>
                      {r.title_en_preview || r.slug}
                    </div>
                    {rs.error && (
                      <div className="text-[10px] text-red-300 mt-0.5" data-testid={`lh-row-error-${r.slug}`}>
                        ✗ {rs.error}
                      </div>
                    )}
                  </td>
                  <td className="text-center">{r.fields.title.nl ? "✓" : "—"}</td>
                  <td className="text-center">{r.fields.body.nl ? "✓" : "—"}</td>
                  <td className="text-center">{r.fields.excerpt.nl ? "✓" : "—"}</td>
                  <td className="pr-2">
                    <span data-testid={`lh-row-cause-${r.slug}`} className="text-[10px] opacity-80">
                      {r.root_cause}
                    </span>
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => handleRetranslate(r.id, r.slug)}
                      disabled={rs.busy}
                      data-testid={`lh-row-retranslate-${r.slug}`}
                      className="text-[9px] tracking-[0.24em] uppercase mr-2 opacity-80 hover:opacity-100 disabled:opacity-40 border border-current px-1.5 py-0.5"
                    >
                      {rs.busy ? "…" : (t("admin.journal.localization.col.retranslate") || "Retranslate")}
                    </button>
                    <button
                      type="button"
                      onClick={() => onEditSlug(r.slug)}
                      data-testid={`lh-row-edit-${r.slug}`}
                      className="text-[9px] tracking-[0.24em] uppercase underline underline-offset-2 opacity-80 hover:opacity-100"
                    >
                      {t("admin.journal.localization.col.fix") /* "Fix" */}
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function JournalAdmin() {
  const navigate = useNavigate();
  const { t } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [token] = useState(() => readAdminToken());
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Explicit founder confirmation of editorial warnings (per opened article)
  const [warningsAck, setWarningsAck] = useState(false);
  useEffect(() => { setWarningsAck(false); }, [form.id]);
  const [showAssetPicker, setShowAssetPicker] = useState(false);

  // Public-site localization audit — populated by the diagnostic endpoint.
  // Drives the "Localization health" panel that shows why the public NL
  // site is (or isn't) actually serving Dutch content.
  const [localAudit, setLocalAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const editorRef = useRef(null);

  const loadLocalizationAudit = async () => {
    if (!token) return;
    setAuditLoading(true);
    try {
      const d = await api("GET", "/api/admin/journal/localization-audit", token);
      setLocalAudit(d);
    } catch (e) {
      // Non-fatal — the panel just doesn't render. Log so we notice.
      // eslint-disable-next-line no-console
      console.warn("localization-audit failed:", e.message);
      setLocalAudit(null);
    } finally {
      setAuditLoading(false);
    }
  };

  const loadList = async () => {
    if (!token) return;
    try {
      const d = await api("GET", "/api/admin/journal/articles", token);
      setArticles(d.articles || []);
    } catch (e) {
      setErr(e.message);
      if (String(e.message).startsWith("401")) {
        clearAdminToken();
        navigate("/admin", { replace: true });
      }
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadList(); loadLocalizationAudit(); }, [token]);

  // Deep-link: /admin/journal?edit=<idOrSlug> — auto-open that article.
  useEffect(() => {
    const key = searchParams.get("edit");
    if (!key || articles.length === 0) return;
    const hit = articles.find((a) => a.id === key || a.slug === key);
    if (hit) {
      setForm(fromDoc(hit));
      setMsg(t("admin.journal.msg.editing", { title: (hit.title && (hit.title.en || hit.title.nl)) || hit.slug }));
      requestAnimationFrame(() => {
        if (editorRef.current) editorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      // Clear the param so a subsequent manual "New" doesn't re-trigger.
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles, searchParams, setSearchParams]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const startNew = () => { setForm(EMPTY_FORM); setMsg(""); setErr(""); };
  const startEdit = (doc) => {
    setForm(fromDoc(doc));
    setMsg(t("admin.journal.msg.editing", { title: (doc.title && (doc.title.en || doc.title.nl)) || doc.slug }));
    setErr("");
    // Scroll editor into view — on mobile the list sits below the form,
    // on desktop the form is offscreen when the list is scrolled to.
    requestAnimationFrame(() => {
      if (editorRef.current) {
        editorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  };

  const save = async ({ publish = false } = {}) => {
    setBusy(true); setMsg(""); setErr("");
    try {
      let saved;
      if (form.id) {
        saved = await api("PATCH", `/api/admin/journal/articles/${form.id}`, token, toPayload(form));
      } else {
        saved = await api("POST", "/api/admin/journal/articles", token, toPayload(form));
        setForm((prev) => ({ ...prev, id: saved.id, slug: saved.slug }));
      }
      if (publish) {
        saved = await api("POST", `/api/admin/journal/articles/${saved.id}/publish`, token,
          form.source === "autoseo" ? { acknowledge_flags: warningsOk && editorialWarnings.length > 0 } : undefined);
      }
      setMsg(`Saved (${saved.status})`);
      await loadList();
    } catch (e) {
      const m = String(e.message || e);
      setErr(/founder approval|approve/i.test(m) ? t("admin.journal.approval.blocked") : m);
    } finally { setBusy(false); }
  };

  // Founder Approval — persists immediately via the existing
  // /approve endpoint (founder_approved_en / founder_approved_nl).
  const toggleApproval = async (lang) => {
    if (!form.id) return;
    const key = `founder_approved_${lang}`;
    const next = !form[key];
    setErr(""); setMsg("");
    try {
      await api("POST", `/api/admin/journal/articles/${form.id}/approve`, token, { lang, approved: next });
      setForm((prev) => ({ ...prev, [key]: next }));
      await loadList();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };
  const bothApproved = form.founder_approved_en && form.founder_approved_nl;
  const cleanupOk = form.source !== "autoseo" || !!form.cleanup_passed;
  const editorialWarnings = collectEditorialWarnings(form);
  const warningsOk = editorialWarnings.length === 0 || form.content_flags_acknowledged || warningsAck;
  const canPublish = bothApproved && cleanupOk && warningsOk;

  // Mandatory AutoSEO cleanup protocol — run/re-run on the open article.
  const runCleanup = async () => {
    if (!form.id) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const res = await api("POST", `/api/admin/journal/articles/${form.id}/cleanup`, token);
      const doc = await api("GET", `/api/admin/journal/articles/${form.id}`, token);
      setForm(fromDoc(doc));
      setMsg(res.cleanup_passed ? t("admin.journal.cleanup.msgPassed") : t("admin.journal.cleanup.msgFailed"));
      await loadList();
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };

  const togglePublish = async (doc) => {
    setBusy(true); setErr("");
    try {
      if (doc.status === "published") {
        await api("POST", `/api/admin/journal/articles/${doc.id}/unpublish`, token);
      } else if (doc.source === "autoseo") {
        // Incident 2026-07-16 safeguard: explicit Founder approval of BOTH
        // language versions, and acknowledgement of any content flags.
        if (!window.confirm("Approve the ENGLISH version of this AutoSEO article for publication?")) { setBusy(false); return; }
        if (!window.confirm("Approve the DUTCH version of this AutoSEO article for publication?")) { setBusy(false); return; }
        const flagsRaw = { ...(doc.content_flags || {}) };
        delete flagsRaw.externalDomains; // physically removed by the cleanup protocol
        const review = ((doc.cleanup_report && doc.cleanup_report.review_flags) || []).map((f) => f.passage);
        if (review.length) flagsRaw.unverifiedClaims = review;
        const flags = Object.keys(flagsRaw).length ? flagsRaw : null;
        if (flags && !window.confirm(
          `This article carries editorial warnings:\n${JSON.stringify(flags, null, 1).slice(0, 900)}\n\nConfirm explicitly and publish?`)) { setBusy(false); return; }
        await api("POST", `/api/admin/journal/articles/${doc.id}/publish`, token,
          { approve_en: true, approve_nl: true, acknowledge_flags: !!flags });
      } else {
        await api("POST", `/api/admin/journal/articles/${doc.id}/publish`, token);
      }
      await loadList();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${(doc.title && doc.title.en) || doc.slug}" permanently?`)) return;
    setBusy(true); setErr("");
    try {
      await api("DELETE", `/api/admin/journal/articles/${doc.id}`, token);
      if (form.id === doc.id) startNew();
      await loadList();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const drafts = useMemo(() => articles
    .filter((a) => a.status !== "published")
    .slice()
    .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")), [articles]);
  const published = useMemo(() => {
    const refNum = (a) => {
      const m = /(\d+)$/.exec(a.article_reference || "");
      return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
    };
    return articles
      .filter((a) => a.status === "published")
      .slice()
      .sort((a, b) => refNum(a) - refNum(b) || (a.published_at || "").localeCompare(b.published_at || ""));
  }, [articles]);

  // Filter for translation status
  const [transFilter, setTransFilter] = useState("all"); // all | missing_nl | missing_en
  const [refQuery, setRefQuery] = useState("");
  const matchesFilters = useMemo(() => {
    const q = refQuery.trim().toLowerCase();
    return (a) => {
      if (q && !(
        (a.article_reference || "").toLowerCase().includes(q) ||
        ((a.title && a.title.en) || "").toLowerCase().includes(q) ||
        ((a.title && a.title.nl) || "").toLowerCase().includes(q) ||
        (a.slug || "").toLowerCase().includes(q))) return false;
      if (transFilter === "missing_nl") {
        const s = translationStatus(a);
        return s === "en_only" || s === "none";
      }
      if (transFilter === "missing_en") {
        const s = translationStatus(a);
        return s === "nl_only" || s === "none";
      }
      return true;
    };
  }, [refQuery, transFilter]);
  const publishedFiltered = useMemo(() => published.filter(matchesFilters), [published, matchesFilters]);
  const draftsFiltered = useMemo(() => drafts.filter(matchesFilters), [drafts, matchesFilters]);
  const renderArticleRow = (doc) => (
    <div key={doc.id} className={`border p-3 ${doc.status === "published" ? "border-gold/40 bg-gold/5" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-serif text-bone text-sm truncate">{(doc.title && (doc.title.en || doc.title.nl)) || doc.slug}</p>
          <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
            {doc.article_reference && (
              <span data-testid={`admin-ref-${doc.slug}`} className="text-[10px] font-mono text-gold/90 border border-gold/30 px-1.5 py-0.5">
                {doc.article_reference}
              </span>
            )}
            <span className="text-[10px] tracking-[0.22em] uppercase text-fog/70">
              {doc.category} · {doc.status === "published" ? t("admin.label.published") : t("admin.label.draft")}
            </span>
            <TranslationBadge article={doc} />
          </div>
        </div>
      </div>
      <div className="mt-2 flex gap-2">
        <button onClick={() => startEdit(doc)} data-testid={`admin-edit-${doc.slug}`} title={t("admin.action.edit")} className="p-1.5 text-bone/70 hover:text-gold"><Edit3 size={14} strokeWidth={1.5} /></button>
        <button onClick={() => togglePublish(doc)} data-testid={`admin-toggle-${doc.slug}`} title={doc.status === "published" ? t("admin.action.unpublish") : t("admin.action.publish")} className="p-1.5 text-bone/70 hover:text-gold">
          {doc.status === "published" ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
        </button>
        <button onClick={() => remove(doc)} data-testid={`admin-delete-${doc.slug}`} title={t("admin.action.delete")} className="p-1.5 text-bone/70 hover:text-red-400"><Trash2 size={14} strokeWidth={1.5} /></button>
      </div>
    </div>
  );

  // Compute translation status for the current editor form (article being edited).
  const editingStatus = translationStatus({
    title: { en: form.title_en, nl: form.title_nl },
  });
  const showNlWarning = editingStatus === "en_only";
  const showEnWarning = editingStatus === "nl_only";

  const copyEnToNl = () => {
    setForm((prev) => ({
      ...prev,
      title_nl: prev.title_nl || prev.title_en,
      excerpt_nl: prev.excerpt_nl || prev.excerpt_en,
      body_nl: prev.body_nl || prev.body_en,
      meta_nl: prev.meta_nl || prev.meta_en,
    }));
    setMsg(t("admin.journal.trans.copied"));
  };

  // ---- AI Translate EN → NL (Claude Sonnet 4.6) ----
  // Runs as a backend background job + polling so long articles can never
  // hit the Cloudflare ~100s proxy timeout. Nothing is saved on failure.
  const [translating, setTranslating] = useState(false);
  const sanitizeErr = (e) => {
    const m = String(e?.message || e || "");
    if (!m || /^\s*</.test(m) || /<html|<!doctype|cloudflare/i.test(m)) {
      return t("admin.journal.trans.ai.failed");
    }
    return m.slice(0, 300);
  };
  const aiTranslate = async () => {
    if (!form.id) {
      setErr(t("admin.journal.trans.ai.needSave"));
      return;
    }
    setTranslating(true);
    setErr("");
    setMsg(t("admin.journal.trans.ai.working"));
    try {
      await api("POST", `/api/admin/journal/articles/${form.id}/translate-async`, token);
      const deadline = Date.now() + 10 * 60 * 1000;
      let job = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        job = await api("GET", `/api/admin/journal/articles/${form.id}/translate-status`, token);
        if (job.status === "done" || job.status === "error") break;
      }
      if (!job || job.status === "running") {
        setMsg("");
        setErr(t("admin.journal.trans.ai.stillRunning"));
        return;
      }
      if (job.status === "error") {
        setMsg("");
        setErr(job.error || t("admin.journal.trans.ai.failed"));
        return;
      }
      const nl = job.translated || {};
      setForm((prev) => ({
        ...prev,
        title_nl: nl.title || prev.title_nl,
        excerpt_nl: nl.excerpt || prev.excerpt_nl,
        body_nl: nl.body || prev.body_nl,
        meta_nl: nl.meta_description || prev.meta_nl,
      }));
      setMsg(t("admin.journal.trans.ai.done"));
    } catch (e) {
      setMsg("");
      setErr(sanitizeErr(e));
    } finally {
      setTranslating(false);
    }
  };

  // ---- BULK: Translate every article that has any missing NL field ----
  // Runs sequentially so the founder sees per-article progress and the LLM
  // API isn't hit with a burst. Each call goes through the same
  // POST /translate?save=true endpoint that the single-article button uses.
  const isMissingNl = (a) => {
    const t = a.title || {};
    const ex = a.excerpt || {};
    const b = a.body || {};
    const md = a.meta_description || {};
    const empty = (v) => !v || !String(v).trim();
    return empty(t.nl) || empty(ex.nl) || empty(b.nl) || empty(md.nl);
  };
  const missingNlArticles = useMemo(() => articles.filter(isMissingNl), [articles]);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResults, setBulkResults] = useState([]); // {id, slug, title, status: 'pending'|'running'|'ok'|'error', message?}
  const bulkAbortRef = useRef(false);

  const openBulk = () => {
    setBulkResults(
      missingNlArticles.map((a) => ({
        id: a.id,
        slug: a.slug,
        title: (a.title && (a.title.en || a.title.nl)) || a.slug,
        status: "pending",
      }))
    );
    setBulkOpen(true);
    bulkAbortRef.current = false;
  };

  const runBulk = async () => {
    setBulkRunning(true);
    bulkAbortRef.current = false;
    for (let i = 0; i < bulkResults.length; i++) {
      if (bulkAbortRef.current) break;
      const row = bulkResults[i];
      // Skip rows already translated successfully — supports "Retry failed"
      if (row.status === "ok") continue;
      // Reset error state and mark running
      setBulkResults((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: "running", message: undefined, status_code: undefined, kind: undefined } : r)));
      try {
        await api("POST", `/api/admin/journal/articles/${row.id}/translate?save=true`, token);
        setBulkResults((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: "ok" } : r)));
      } catch (e) {
        const msg = String(e.message || e);
        const status_code = e.status || null;
        // Best-effort classification for the badge above the pre block
        let kind = "server error";
        if (status_code === 401) kind = "unauthorized";
        else if (status_code === 404) kind = "article not found";
        else if (status_code === 400) kind = "bad request";
        else if (status_code === 502) kind = "llm error";
        else if (status_code === 500 && /EMERGENT_LLM_KEY/i.test(msg)) kind = "missing key";
        else if (status_code === 500) kind = "server error";
        else if (!status_code) kind = "network error";
        setBulkResults((prev) =>
          prev.map((r, idx) =>
            idx === i ? { ...r, status: "error", message: msg, status_code, kind } : r
          )
        );
      }
    }
    setBulkRunning(false);
    // Refresh the list so badges/warnings update
    loadList();
  };

  const cancelBulk = () => {
    bulkAbortRef.current = true;
  };

  const closeBulk = () => {
    if (bulkRunning) return;
    setBulkOpen(false);
    setBulkResults([]);
  };

  return (
    <AdminShell title={t("admin.journal.title")}>
      <div data-testid="admin-journal">
        <div className="mb-8">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors"
            data-testid="admin-journal-back"
          >
            <ArrowLeft size={12} strokeWidth={1.5} /> {t("admin.journal.backToDash")}
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-10">
          {/* Editor */}
          <section ref={editorRef} className="min-w-0 scroll-mt-32">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-bone">
                {form.id ? t("admin.journal.editor.edit") : t("admin.journal.editor.new")}
                {form.article_reference && (
                  <span data-testid="editor-article-reference" className="ml-3 align-middle text-[11px] font-mono text-gold/90 border border-gold/30 px-2 py-0.5">
                    {form.article_reference}
                  </span>
                )}
              </h2>
              {form.id && <button onClick={startNew} className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gold hover:text-bone transition-colors" data-testid="admin-new"><Plus size={12}/> {t("admin.action.new")}</button>}
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-gold mb-2">{t("admin.journal.editor.category")}</label>
                <select value={form.category} onChange={set("category")} data-testid="admin-category" className="w-full bg-black border border-gold/40 text-bone px-3 py-2.5 focus:outline-none focus:border-gold">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-gold mb-2">{t("admin.journal.editor.slug")}</label>
                <input value={form.slug} onChange={set("slug")} data-testid="admin-slug" placeholder="auto" className="w-full bg-black border border-gold/40 text-bone px-3 py-2.5 focus:outline-none focus:border-gold" />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-gold">{t("admin.journal.editor.cover")}</label>
                  <button
                    type="button"
                    onClick={() => setShowAssetPicker(true)}
                    data-testid="admin-cover-vault-pick"
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/40 px-2.5 py-1 hover:bg-gold/10"
                  >
                    <Sparkles size={11} strokeWidth={1.75} /> {t("admin.journal.editor.pickVault")}
                  </button>
                </div>
                {form.cover_content_id && (
                  <div className="mb-2 flex items-center gap-2 border border-gold/30 bg-gold/[0.03] px-2 py-1.5" data-testid="admin-cover-cid">
                    <Sparkles size={11} className="text-gold flex-shrink-0" />
                    <span className="text-[10px] tracking-[0.22em] uppercase text-bone/60">Vault:</span>
                    <span className="text-[11px] text-gold font-mono">{form.cover_content_id}</span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, cover_content_id: "" })}
                      data-testid="admin-cover-cid-clear"
                      className="ml-auto text-bone/50 hover:text-red-400"
                      title="Remove Vault link. The cover URL below stays"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                <input value={form.cover} onChange={set("cover")} data-testid="admin-cover" placeholder="https://…/image.jpg (fallback if no Vault link)" className="w-full bg-black border border-gold/40 text-bone px-3 py-2.5 focus:outline-none focus:border-gold" />
                {form.cover_content_id ? (
                  <div className="mt-3 border border-white/10">
                    <VaultImage contentId={form.cover_content_id} variant="thumb" admin className="w-full max-h-56 object-cover" data-testid="admin-cover-vault-preview" />
                  </div>
                ) : (form.cover && <img src={form.cover} alt="cover preview" className="mt-3 w-full max-h-56 object-cover border border-white/10" />)}
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] tracking-[0.28em] uppercase text-gold mb-2">{t("admin.journal.editor.tags")}</label>
                <input value={form.tags} onChange={set("tags")} data-testid="admin-tags" placeholder="stoicism, resilience, marcus" className="w-full bg-black border border-gold/40 text-bone px-3 py-2.5 focus:outline-none focus:border-gold" />
                <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="admin-tag-chips">
                  {TAG_GROUPS.flatMap((g) => g.tags).map((tag) => {
                    const listed = (form.tags || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
                    const on = listed.includes(tag.toLowerCase());
                    const toggle = () => setForm((f) => {
                      const parts = (f.tags || "").split(",").map((s) => s.trim()).filter(Boolean);
                      const next = on ? parts.filter((p) => p.toLowerCase() !== tag.toLowerCase()) : [...parts, tag];
                      return { ...f, tags: next.join(", ") };
                    });
                    return (
                      <button key={tag} type="button" onClick={toggle}
                        data-testid={`admin-tag-chip-${tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`px-2.5 py-1 text-[10px] tracking-[0.14em] uppercase border transition-colors ${on ? "border-gold text-gold bg-gold/10" : "border-white/15 text-bone/50 hover:border-gold/40 hover:text-bone"}`}>
                        {tag}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.28em] uppercase text-gold mb-2">Reading time (min)</label>
                <input type="number" min={1} max={60} value={form.reading_time_min} onChange={set("reading_time_min")} data-testid="admin-reading-time" className="w-full bg-black border border-gold/40 text-bone px-3 py-2.5 focus:outline-none focus:border-gold" />
              </div>
              <label className="flex items-center gap-3 self-end">
                <input type="checkbox" checked={form.featured} onChange={set("featured")} data-testid="admin-featured" className="accent-[#C8A96A]" />
                <span className="text-[10px] tracking-[0.28em] uppercase text-gold">{t("admin.journal.editor.featured")}</span>
              </label>
            </div>

            {/* Content columns */}
            {[
              { l: "EN", suffix: "en", desc: "English" },
              { l: "NL", suffix: "nl", desc: "Nederlands" },
            ].map(({ l, suffix, desc }) => {
              const isNl = suffix === "nl";
              const showWarn = (isNl && showNlWarning) || (!isNl && showEnWarning);
              return (
                <div key={suffix} className="mt-6 border border-white/10 p-4 md:p-5" data-testid={`admin-lang-block-${suffix}`}>
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <p className="text-[10px] tracking-[0.32em] uppercase text-gold">{l} · {desc}</p>
                    {isNl && (form.title_en || form.body_en) && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={aiTranslate}
                          disabled={translating || !form.id}
                          data-testid="admin-ai-translate-en-to-nl"
                          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/50 px-2.5 py-1 hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed"
                          title={form.id ? t("admin.journal.trans.ai.button") : t("admin.journal.trans.ai.needSave")}
                        >
                          {translating ? (
                            <Loader2 size={11} strokeWidth={1.75} className="animate-spin" />
                          ) : (
                            <Sparkles size={11} strokeWidth={1.75} />
                          )}
                          {translating ? t("admin.journal.trans.ai.working_short") : t("admin.journal.trans.ai.button")}
                        </button>
                        <button
                          type="button"
                          onClick={copyEnToNl}
                          data-testid="admin-copy-en-to-nl"
                          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-bone/70 border border-white/15 px-2.5 py-1 hover:text-gold hover:border-gold/40"
                          title={t("admin.journal.trans.copy")}
                        >
                          <Copy size={11} strokeWidth={1.5} /> {t("admin.journal.trans.copy")}
                        </button>
                      </div>
                    )}
                  </div>
                  {showWarn && (
                    <div
                      data-testid={`admin-trans-warn-${suffix}`}
                      className="mb-4 flex items-start gap-3 border border-gold/40 bg-gold/5 px-3 py-2.5"
                    >
                      <AlertTriangle size={14} strokeWidth={1.75} className="text-gold flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-[11px] tracking-[0.2em] uppercase text-gold font-medium">
                          {isNl ? t("admin.journal.trans.warn.title") : t("admin.journal.trans.warn.title_en")}
                        </p>
                        <p className="mt-1 text-[12px] text-bone/75 leading-relaxed">
                          {isNl ? t("admin.journal.trans.warn.body") : t("admin.journal.trans.warn.body_en")}
                        </p>
                      </div>
                    </div>
                  )}
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-bone/70 mb-2">{t("admin.journal.editor.title")}</label>
                  <input value={form[`title_${suffix}`]} onChange={set(`title_${suffix}`)} data-testid={`admin-title-${suffix}`} className="w-full bg-black border border-gold/30 text-bone px-3 py-2.5 mb-3 focus:outline-none focus:border-gold" />
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-bone/70 mb-2">{t("admin.journal.editor.excerpt")}</label>
                  <textarea value={form[`excerpt_${suffix}`]} onChange={set(`excerpt_${suffix}`)} data-testid={`admin-excerpt-${suffix}`} rows={3} className="w-full bg-black border border-gold/30 text-bone px-3 py-2.5 mb-3 focus:outline-none focus:border-gold resize-y" />
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-bone/70 mb-2">{t("admin.journal.editor.body")} (Markdown)</label>
                  <textarea value={form[`body_${suffix}`]} onChange={set(`body_${suffix}`)} data-testid={`admin-body-${suffix}`} rows={14} className="w-full bg-black border border-gold/30 text-bone px-3 py-2.5 font-mono text-sm mb-3 focus:outline-none focus:border-gold resize-y" placeholder="Markdown…" />
                  <label className="block text-[10px] tracking-[0.28em] uppercase text-bone/70 mb-2">{t("admin.journal.editor.seoDescription")}</label>
                  <textarea value={form[`meta_${suffix}`]} onChange={set(`meta_${suffix}`)} data-testid={`admin-meta-${suffix}`} rows={2} className="w-full bg-black border border-gold/30 text-bone px-3 py-2.5 focus:outline-none focus:border-gold resize-y" />
                </div>
              );
            })}

            {/* Mandatory AutoSEO cleanup protocol — report + gate */}
            {form.id && form.source === "autoseo" && (
              <CleanupReportPanel report={form.cleanup_report} passed={!!form.cleanup_passed}
                onRun={runCleanup} busy={busy} t={t} />
            )}

            {/* Editorial warnings — explicit founder confirmation before Publish */}
            {form.id && form.source === "autoseo" && editorialWarnings.length > 0 && (
              <div className={`mt-8 p-4 border ${warningsOk ? "border-emerald-400/40 bg-emerald-400/[0.04]" : "border-amber-400/40 bg-amber-400/[0.04]"}`} data-testid="editorial-warnings-section">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h3 className="text-[11px] tracking-[0.28em] uppercase text-bone/80">{t("admin.journal.warnings.title")}</h3>
                  <span className={`text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 border ${warningsOk ? "border-emerald-400/50 text-emerald-300" : "border-amber-400/50 text-amber-300"}`} data-testid="warnings-status-badge">
                    {warningsOk ? t("admin.journal.warnings.confirmed") : t("admin.journal.warnings.open")}
                  </span>
                </div>
                <p className="text-[12px] text-bone/60 mb-3">{t("admin.journal.warnings.intro")}</p>
                <ul className="text-[12px] text-bone/75 space-y-1.5 mb-4" data-testid="editorial-warnings-list">
                  {editorialWarnings.slice(0, 12).map((w, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-amber-300 shrink-0">⚑</span>
                      <span>
                        <span className="text-[10px] tracking-[0.18em] uppercase text-amber-300/90 mr-2">{t(WARNING_TYPE_KEYS[w.type] || "admin.journal.warnings.claim")}</span>
                        “{w.passage}”
                      </span>
                    </li>
                  ))}
                </ul>
                {form.content_flags_acknowledged ? (
                  <p className="text-[12px] text-emerald-300" data-testid="warnings-already-ack">{t("admin.journal.warnings.alreadyAck")}</p>
                ) : (
                  <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="warnings-ack-label">
                    <input
                      type="checkbox"
                      checked={warningsAck}
                      onChange={(e) => setWarningsAck(e.target.checked)}
                      data-testid="warnings-ack-checkbox"
                      className="mt-0.5 h-4 w-4 accent-amber-400"
                    />
                    <span className="text-[12px] text-bone/80">{t("admin.journal.warnings.ackLabel")}</span>
                  </label>
                )}
              </div>
            )}

            {/* Founder Approval — gate before Publish */}
            <div className={`mt-8 p-4 border ${canPublish ? "border-emerald-400/40 bg-emerald-400/[0.04]" : "border-amber-400/40 bg-amber-400/[0.04]"}`} data-testid="founder-approval-section">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h3 className="text-[11px] tracking-[0.28em] uppercase text-bone/80">{t("admin.journal.approval.title")}</h3>
                <span className={`text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 border ${canPublish ? "border-emerald-400/50 text-emerald-300" : "border-amber-400/50 text-amber-300"}`} data-testid="approval-status-badge">
                  {canPublish ? t("admin.journal.approval.ready") : (!warningsOk && bothApproved ? t("admin.journal.warnings.pending") : t("admin.journal.approval.required"))}
                </span>
              </div>
              {!form.id ? (
                <p className="text-[12px] text-bone/50" data-testid="approval-save-first">{t("admin.journal.approval.saveFirst")}</p>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-8">
                  {["en", "nl"].map((lg) => (
                    <label key={lg} className="flex items-center gap-3 cursor-pointer select-none" data-testid={`approval-label-${lg}`}>
                      <input
                        type="checkbox"
                        checked={!!form[`founder_approved_${lg}`]}
                        onChange={() => toggleApproval(lg)}
                        data-testid={`approval-checkbox-${lg}`}
                        className="h-4 w-4 accent-[#C9A55C] cursor-pointer"
                      />
                      <span className="text-sm text-bone/80">{t(`admin.journal.approval.${lg}`)}</span>
                      {form[`founder_approved_${lg}`] && <Check size={13} strokeWidth={2} className="text-emerald-300" />}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button onClick={() => save({ publish: false })} disabled={busy} data-testid="admin-save-draft" className="inline-flex items-center gap-2 border border-gold/50 text-gold px-5 py-3 text-[11px] tracking-[0.24em] uppercase hover:bg-gold/10 disabled:opacity-50">
                <Save size={14} strokeWidth={1.5} /> {t("admin.action.saveDraft")}
              </button>
              <button onClick={() => save({ publish: true })} disabled={busy || !canPublish} data-testid="admin-publish"
                title={canPublish ? "" : (!cleanupOk ? t("admin.journal.cleanup.blocked") : (!warningsOk ? t("admin.journal.warnings.blocked") : t("admin.journal.approval.required")))}
                className="inline-flex items-center gap-2 bg-gold text-ink px-5 py-3 text-[11px] tracking-[0.24em] uppercase hover:bg-gold-hover disabled:opacity-50 disabled:cursor-not-allowed">
                <Check size={14} strokeWidth={1.75} /> {t("admin.action.publish")}
              </button>
              {msg && <span className="text-sm text-gold ml-2" data-testid="admin-msg">{msg}</span>}
              {err && <span className="text-sm text-red-400 ml-2" data-testid="admin-err">{err}</span>}
            </div>
          </section>

          {/* List */}
          <aside className="min-w-0">
            <h2 className="font-serif text-2xl text-bone mb-4">{t("admin.action.viewAllArticles")}</h2>
            <input
              type="text"
              value={refQuery}
              onChange={(e) => setRefQuery(e.target.value)}
              placeholder="Search title, slug or ART-0011…"
              data-testid="admin-article-search"
              className="w-full mb-4 bg-white/[0.03] border border-white/15 focus:border-gold/60 outline-none px-3 py-2.5 text-sm text-bone placeholder:text-bone/30"
            />

            {/* Localization Health — Root-cause diagnostic for the P0
                "NL switch shows English content" bug. Shows why the public
                site's language switch appears to fail (spoiler: it doesn't —
                the affected articles just have empty NL fields in the DB). */}
            <LocalizationHealth
              audit={localAudit}
              loading={auditLoading}
              onRefresh={loadLocalizationAudit}
              onOpenBulk={openBulk}
              onEditSlug={(slug) => {
                const hit = articles.find((a) => a.slug === slug);
                if (hit) startEdit(hit);
              }}
              t={t}
            />

            {/* Bulk translate missing NL */}
            {missingNlArticles.length > 0 && (
              <div className="mb-4 border border-gold/40 bg-gold/5 p-3" data-testid="admin-bulk-nl-panel">
                <p className="text-[11px] tracking-[0.24em] uppercase text-gold mb-1.5 font-medium">
                  {t("admin.journal.bulk.title", { count: missingNlArticles.length })}
                </p>
                <p className="text-[12px] text-bone/75 leading-relaxed mb-3">
                  {t("admin.journal.bulk.body")}
                </p>
                <button
                  type="button"
                  onClick={openBulk}
                  data-testid="admin-bulk-nl-open"
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/60 px-3 py-1.5 hover:bg-gold/10"
                >
                  <Sparkles size={11} strokeWidth={1.75} /> {t("admin.journal.bulk.button")}
                </button>
              </div>
            )}

            {/* Translation filter */}
            <div className="mb-4" data-testid="admin-trans-filter">
              <label className="block text-[10px] tracking-[0.28em] uppercase text-gold/80 mb-2">
                {t("admin.journal.trans.filter.label")}
              </label>
              <select
                value={transFilter}
                onChange={(e) => setTransFilter(e.target.value)}
                data-testid="admin-trans-filter-select"
                className="w-full bg-black border border-gold/40 text-bone px-3 py-2 text-sm focus:outline-none focus:border-gold"
              >
                <option value="all">{t("admin.journal.trans.filter.all")}</option>
                <option value="missing_nl">{t("admin.journal.trans.filter.missing_nl")}</option>
                <option value="missing_en">{t("admin.journal.trans.filter.missing_en")}</option>
              </select>
            </div>

            <div className="space-y-6" data-testid="admin-article-list">
              {[
                { key: "published", label: t("admin.journal.section.published"), rows: publishedFiltered, testid: "admin-published-section" },
                { key: "drafts", label: t("admin.journal.section.drafts"), rows: draftsFiltered, testid: "admin-drafts-section" },
              ].map((sec) => (
                <section key={sec.key} data-testid={sec.testid}>
                  <h3 className="text-[10px] tracking-[0.28em] uppercase text-gold/80 mb-2 border-b border-white/10 pb-1.5">
                    {sec.label} ({sec.rows.length})
                  </h3>
                  <div className="space-y-3">
                    {sec.rows.map((doc) => renderArticleRow(doc))}
                    {sec.rows.length === 0 && <p className="text-fog/50 text-xs">—</p>}
                  </div>
                </section>
              ))}
              {articles.length === 0 && <p className="text-fog/60 text-sm">{t("admin.journal.list.empty")}</p>}
            </div>
          </aside>
        </div>
      </div>

      {/* Bulk Translate Modal */}
      {bulkOpen && (
        <div
          data-testid="admin-bulk-modal"
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeBulk}
        >
          <div
            className="bg-ink border border-gold/40 max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <p className="text-[11px] tracking-[0.28em] uppercase text-gold">{t("admin.journal.bulk.modal.eyebrow")}</p>
                <h3 className="font-serif text-xl text-bone mt-1">{t("admin.journal.bulk.modal.title", { count: bulkResults.length })}</h3>
              </div>
              <button
                type="button"
                onClick={closeBulk}
                disabled={bulkRunning}
                data-testid="admin-bulk-close"
                className="p-2 text-bone/60 hover:text-bone disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            {/* Progress list */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2" data-testid="admin-bulk-list">
              {bulkResults.map((r) => (
                <div
                  key={r.id}
                  data-testid={`admin-bulk-row-${r.slug}`}
                  className={`border p-3 ${
                    r.status === "ok"
                      ? "border-emerald-400/40 bg-emerald-400/5"
                      : r.status === "error"
                      ? "border-red-400/50 bg-red-400/5"
                      : r.status === "running"
                      ? "border-gold/50 bg-gold/5"
                      : "border-white/10 bg-white/[0.02]"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {r.status === "pending" && <span className="block w-3 h-3 border border-fog/50 rounded-full" />}
                      {r.status === "running" && <Loader2 size={14} strokeWidth={1.75} className="text-gold animate-spin" />}
                      {r.status === "ok" && <CheckCircle2 size={14} strokeWidth={1.75} className="text-emerald-400" />}
                      {r.status === "error" && <XCircle size={14} strokeWidth={1.75} className="text-red-400" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-sm text-bone truncate">{r.title}</p>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-fog/70 mt-0.5 truncate">{r.slug}</p>
                    </div>
                    {r.status === "error" && r.message && (
                      <button
                        type="button"
                        onClick={() => { try { navigator.clipboard.writeText(r.message); } catch { /* noop */ } }}
                        data-testid={`admin-bulk-copy-error-${r.slug}`}
                        className="flex-shrink-0 text-[9px] tracking-[0.24em] uppercase text-red-300/80 border border-red-400/40 px-2 py-1 hover:text-red-200 hover:bg-red-400/10"
                        title="Copy full error to clipboard"
                      >
                        <Copy size={10} strokeWidth={1.75} className="inline mr-1" />
                        {t("admin.journal.bulk.copyError")}
                      </button>
                    )}
                  </div>
                  {r.status === "error" && r.message && (
                    <div
                      data-testid={`admin-bulk-error-${r.slug}`}
                      className="mt-2.5 pl-6 pr-1"
                    >
                      {r.status_code && (
                        <p className="text-[10px] tracking-[0.22em] uppercase text-red-300/70 mb-1.5">
                          {t("admin.journal.bulk.errorKind", { kind: r.kind || "server error" })} · HTTP {r.status_code}
                        </p>
                      )}
                      <pre className="text-[11.5px] leading-relaxed text-red-100/90 bg-red-950/30 border border-red-400/20 p-2.5 whitespace-pre-wrap break-words font-mono max-h-40 overflow-y-auto">
                        {r.message}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
              {bulkResults.length === 0 && (
                <p className="text-fog/60 text-sm text-center py-4">—</p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10">
              <p className="text-[11px] tracking-[0.24em] uppercase text-fog/70">
                {t("admin.journal.bulk.modal.progress", {
                  done: bulkResults.filter((r) => r.status === "ok" || r.status === "error").length,
                  total: bulkResults.length,
                })}
              </p>
              <div className="flex items-center gap-2">
                {bulkRunning ? (
                  <button
                    type="button"
                    onClick={cancelBulk}
                    data-testid="admin-bulk-cancel"
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-bone/70 border border-white/20 px-3 py-1.5 hover:text-bone"
                  >
                    {t("admin.journal.bulk.modal.cancel")}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={closeBulk}
                      data-testid="admin-bulk-done"
                      className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-bone/70 border border-white/20 px-3 py-1.5 hover:text-bone"
                    >
                      {t("admin.journal.bulk.modal.close")}
                    </button>
                    <button
                      type="button"
                      onClick={runBulk}
                      disabled={bulkResults.length === 0 || bulkResults.every((r) => r.status === "ok")}
                      data-testid="admin-bulk-start"
                      className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/60 px-3 py-1.5 hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Sparkles size={11} strokeWidth={1.75} />
                      {bulkResults.some((r) => r.status === "ok" || r.status === "error")
                        ? t("admin.journal.bulk.modal.retry")
                        : t("admin.journal.bulk.modal.start")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AssetPicker
        open={showAssetPicker}
        onClose={() => setShowAssetPicker(false)}
        onSelect={(asset) => {
          setForm((f) => ({ ...f, cover_content_id: asset.content_id }));
          setMsg(`Vault cover set: ${asset.content_id}`);
        }}
        title={t("admin.journal.editor.pickVault")}
      />
    </AdminShell>
  );
}
